import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang, pick } from "@/contexts/LanguageContext";
import { pathFor, ROUTE_KEYS, carDetailPath, guideDetailPath } from "@/i18n/routeMap";
import { api } from "@/lib/api";
import { websitePhoneLabel, WEBSITE_PHONE_DEFAULT } from "@/lib/whatsapp";
import Logo from "@/components/Logo";

export default function Footer() {
  const { lang, t } = useLang();
  const location = useLocation();
  const [s, setS] = useState({});
  const [guides, setGuides] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, vehiclesRes] = await Promise.all([
          api.get("/settings"),
          api.get("/vehicles"),
        ]);
        setS(settingsRes.data || {});
        const list = (vehiclesRes.data || [])
          .filter((v) => v.available && v.slug && v.seoEnabled !== false && !v.noindex)
          .slice(0, 6);
        setGuides(list);
      } catch { /* ignore */ }
    })();
  }, []);
  if (location.pathname.startsWith("/admin")) return null;
  const year = new Date().getFullYear();
  const phone = websitePhoneLabel(s) || WEBSITE_PHONE_DEFAULT;

  return (
    <footer className="bg-[#0C0C0C] border-t border-[#2A2A2A] mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <Logo size={56} />
          <p className="mt-5 text-sm text-[#8A8A8A] max-w-md font-light leading-relaxed">{t.footer.tagline}</p>
        </div>

        <div>
          <h4 className="text-overline text-[#C9A55A] mb-5">{t.footer.brand}</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to={pathFor(ROUTE_KEYS.HOME, lang)} className="text-[#F5F1EB] hover:text-[#C9A55A]">{t.nav.catalog}</Link></li>
            <li><Link to={pathFor(ROUTE_KEYS.ROUTES, lang)} className="text-[#F5F1EB] hover:text-[#C9A55A]">{t.nav.routes}</Link></li>
            <li><Link to={pathFor(ROUTE_KEYS.CONTACT, lang)} className="text-[#F5F1EB] hover:text-[#C9A55A]">{t.nav.contact}</Link></li>
            <li><Link to={pathFor(ROUTE_KEYS.GUIDES, lang)} className="text-[#F5F1EB] hover:text-[#C9A55A]" data-testid="footer-guides-link">{t.footer.guidesLink || "Guides"}</Link></li>
            <li><Link to={pathFor(ROUTE_KEYS.RENTAL_FAQ, lang)} className="text-[#F5F1EB] hover:text-[#C9A55A]" data-testid="footer-faq-link">FAQ</Link></li>
          </ul>
          {guides.length > 0 && (
            <>
              <h4 className="text-overline text-[#C9A55A] mt-8 mb-3" data-testid="footer-guides-heading">{t.footer.guides}</h4>
              <ul className="space-y-2 text-sm" data-testid="footer-guides-list">
                {guides.map((v) => (
                  <li key={v.id}>
                    <Link to={carDetailPath(v.slug, lang)} className="text-[#F5F1EB] hover:text-[#C9A55A] text-[13px]" data-testid={`footer-guide-${v.slug}`}>
                      {v.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div>
          <h4 className="text-overline text-[#C9A55A] mb-5">{t.footer.contactUs}</h4>
          <ul className="space-y-2 text-sm">
            <li className="text-[#F5F1EB]"><a href={`tel:${phone.replace(/\s/g, "")}`} className="hover:text-[#C9A55A]" data-testid="footer-phone">{phone}</a></li>
            <li className="text-[#F5F1EB]"><a href={`mailto:${s.email || "spainluxuryrentacar@gmail.com"}`} className="hover:text-[#C9A55A]">{s.email || "spainluxuryrentacar@gmail.com"}</a></li>
            <li className="text-[#8A8A8A]">{pick(s.address, lang) || "Benidorm, Alicante, Costa Blanca"}</li>
            <li className="text-[#8A8A8A]">{pick(s.openingHours, lang) || "Open 24H"}</li>
          </ul>
          <h4 className="text-overline text-[#C9A55A] mt-6 mb-3">{t.footer.legal}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to={pathFor(ROUTE_KEYS.RENTAL_CONDITIONS, lang)} className="text-[#F5F1EB] hover:text-[#C9A55A]">{t.legal.rental_conditions}</Link></li>
            <li><Link to={pathFor(ROUTE_KEYS.TERMS, lang)} className="text-[#F5F1EB] hover:text-[#C9A55A]">{t.legal.terms}</Link></li>
            <li><Link to={pathFor(ROUTE_KEYS.PRIVACY, lang)} className="text-[#F5F1EB] hover:text-[#C9A55A]">{t.legal.privacy}</Link></li>
            <li><Link to={pathFor(ROUTE_KEYS.COOKIES, lang)} className="text-[#F5F1EB] hover:text-[#C9A55A]">{t.legal.cookies}</Link></li>
          </ul>
        </div>
      </div>
      <div className="hairline" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row gap-2 justify-between text-xs text-[#8A8A8A]">
        <div>© {year} Spain Luxury Rentals · SLRC. {t.footer.rights}</div>
        <div className="tracking-[0.2em] uppercase">{phone} · {pick(s.address, lang) || "Costa Blanca"}</div>
      </div>
    </footer>
  );
}
